import { css } from '@emotion/core'
import { useLazyQuery } from '@apollo/client/react'
import { trackEvent } from '@fs/zion-analytics'
import zionDebug from '@fs/zion-debug'
import { NoticeLoading } from '@fs/zion-icon'
import { ComboViewer, RibbonViewer, StaticImageViewer } from '@fs/zion-image-viewer'
import { useQueryParams } from '@fs/zion-router'
import { useSlsStateContext } from '@fs/zion-sls'
import { Row, useContainerWidth, useZoomPan } from '@fs/zion-ui'
import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import queries from '../../views/imageBrowse/queries'
import SharedUsabilla from '../../../shared/components/sharedUsabilla/SharedUsabilla'
import { downloadImage, getFileNames, getOptimizedImageProps } from '../../utils/downloadHelpers'
import { useErrorStatus, useGroupData } from '../../views/imageBrowse/hooks'
import { useSharedBrowserStateContext } from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

import useInitialMaintainZoomPan from '../../../shared/hooks/useInitialMaintainZoom'
import useViewMode from '../../views/imageBrowse/hooks/useViewMode'
import ImageGroupError from '../imageGroupError/ImageGroupError'
import Controls from './Controls'
import {
  useImageBrowserGroup,
  useImageBrowserSetup,
  useKeepGroupIdInSync,
  useSections,
  useSetFocusedIndex,
  useSetGridParamInURL,
  useUpdateDividersHoverButtonProps,
  useWatchApidsForThumbnails,
  useWatchViewModeForFocusedIndex,
  useInitializeViewModeFromURL,
} from './hooks'
import DarkModeControl from '../../../shared/components/darkModeControl/DarkModeControl'
import { useDarkModeContext } from '../../../shared/providers/darkModeProvider/DarkModeProvider'
import useAllowDarkMode from '../../../shared/hooks/useAllowDarkMode'

const debug = zionDebug('siv:SharedImageBrowserViewer')

const viewerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
}

// eslint-disable-next-line no-warning-comments
// TODO: remove this when we fix JIRA FRONTIER-911
const viewerWrapperCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  position: relative;
`

const visuallyHiddenCss = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

// TODO: open ticket in zion-image-viewer to fix [is-focused]::after contrast (WCAG 1.4.11/1.4.1)
// and in zion-ui to fix toolbar button height at 200% text zoom (WCAG 1.4.4) so this workaround
// can be removed and every consumer benefits automatically.
// Override grid selection indicator to meet 3:1 non-text contrast (WCAG 1.4.11)
// and add a non-color double-ring indicator visible in greyscale (WCAG 1.4.1).
// Also prevent fixed-height toolbar buttons from clipping at 200% text zoom (WCAG 1.4.4).
const gridA11yCss = css`
  [is-focused]::after {
    border-color: var(--blue50) !important;
    box-shadow:
      inset 0 0 0 3px var(--gray00),
      inset 0 0 0 5px var(--gray90) !important;
  }

  [role='toolbar'] button {
    height: auto;
    min-height: 36px;
  }
`

// TODO: open ticket in zion-image-viewer to fix GridItem's hoverable branch so it keeps
// role="option" instead of role="button", then remove this function and the effect below.
function patchGridAriaRoles(section) {
  section.querySelectorAll('[role="button"][aria-selected]').forEach((el) => {
    el.setAttribute('role', 'option')
  })
}

export default function GroupImage() {
  const [t] = useTranslation()
  const { theme, resetDarkMode } = useDarkModeContext()
  const { query } = useQueryParams({ replaceState: true })
  const [imageViewer] = useZoomPan()
  const atWidth = useContainerWidth()
  const { view = 'explore' } = query

  const groupData = useImageBrowserGroup()
  const sharedBrowserState = useSharedBrowserStateContext()
  const zionSlsState = useSlsStateContext()
  const imageApids = groupData?.imageApids
  const { thumbnails } = sharedBrowserState

  const { loading } = useGroupData()
  const { currentImageArk } = zionSlsState
  const { errorStatus } = useErrorStatus()
  const initialMaintainZoomPanProps = useInitialMaintainZoomPan()
  const [columnCount, setColumnCount] = useState(5)
  const { viewMode, setViewMode } = useViewMode()
  const [getNineFive, { data: nineFive, loading: loadNineFive }] = useLazyQuery(queries.getArtifactNineFive())

  const ribbonImages = useMemo(() => {
    return thumbnails?.map((x) => ({ alt: x.alt, id: x.id, src: x.srcSet?.[0] || '' }))
  }, [thumbnails])

  // TODO: open ticket in zion-image-viewer to expose grid item position via the numbered prop's
  // accessible name so this aria-label and the matching alt in getImagesFromApidList can be removed.
  const imageLabel = useCallback(
    (index, id) => t('GroupImage.gridItemLabel', 'Image {index} of {id}', { index, id }),
    [t]
  )
  const modifiedThumbnails = useMemo(
    () => thumbnails?.map((thumb, idx) => ({ ...thumb, 'aria-label': imageLabel(idx + 1, thumbnails.length) })),
    [thumbnails, imageLabel]
  )

  const [statusMessage, setStatusMessage] = useState('')
  const sectionRef = useRef(null)
  const statusTimerRef = useRef(null)

  useEffect(() => {
    if (thumbnails?.[sharedBrowserState.focusedIndex]) {
      const focusedImage = thumbnails?.[sharedBrowserState.focusedIndex]
      getNineFive({ variables: { imageApid: focusedImage?.id } })
    }
  }, [thumbnails, sharedBrowserState.focusedIndex, getNineFive])

  const handleDownloadEvent = useCallback(() => {
    const focusedImage = thumbnails?.[sharedBrowserState.focusedIndex]
    const { src: bestSrc } = getOptimizedImageProps(focusedImage, Infinity)
    const { downloadUrl, localFileName } = getFileNames(bestSrc)
    let finalFileName = localFileName
    if (localFileName.startsWith('image') && focusedImage?.id) {
      finalFileName = localFileName.replace('image', focusedImage.id)
    }
    const idsFromSiblings =
      groupData?.siblingIdsWithChildren?.map((sibling) => sibling.childIds).flat() || groupData?.imageApids
    const location = idsFromSiblings.indexOf(focusedImage?.id)
    if (location > -1) {
      // Keep the extension from getFileNames, otherwise the browser guesses one from the Blob's MIME type (e.g. .jfif)
      const extension = /\.\w+$/.exec(localFileName)?.[0] || ''
      const baseName = loadNineFive
        ? `${groupData?.imageGroupNumber}_${(location + 1).toString().padStart(5, 0)}`
        : nineFive?.getArtifactNineFive
      // Without a 9_5 number, keep the name built above
      if (baseName) finalFileName = `${baseName}${extension}`
    }
    downloadImage(finalFileName, downloadUrl)
    trackEvent({
      link_name: 'image-download-complete',
    })
  }, [
    groupData?.imageApids,
    groupData?.imageGroupNumber,
    groupData?.siblingIdsWithChildren,
    sharedBrowserState.focusedIndex,
    thumbnails,
    loadNineFive,
    nineFive,
  ])

  /**
   * A Warning:
   * Do not be fooled by the seeming lack of arguments. The following hooks make heavy usage of context
   */
  // IMPORTANT: Call useInitializeViewModeFromURL BEFORE useImageBrowserSetup to avoid race conditions
  useInitializeViewModeFromURL()
  useImageBrowserSetup()
  useAllowDarkMode()
  useWatchViewModeForFocusedIndex({ ribbonImages })
  useWatchApidsForThumbnails()
  const updateDividersHoverButtonProps = useUpdateDividersHoverButtonProps()
  const sections = useSections()
  const setGridParamInURL = useSetGridParamInURL()
  const setFocusIndex = useSetFocusedIndex()
  const setNewGroupId = useKeepGroupIdInSync()
  const setFocusedIndex = useCallback(
    (focusedIndex) => {
      setFocusIndex(focusedIndex)
      setNewGroupId(focusedIndex)
    },
    [setFocusIndex, setNewGroupId]
  )

  const handleAdjustmentControlReset = useCallback(() => {
    resetDarkMode()
  }, [resetDarkMode])

  // Fix 4: Announce image navigation to screen readers
  // TODO: open ticket in zion-image-viewer to add this live region to ComboViewer so every
  // consumer gets navigation announcements automatically.
  useEffect(() => {
    if (sharedBrowserState.focusedIndex >= 0 && thumbnails?.length) {
      clearTimeout(statusTimerRef.current)
      statusTimerRef.current = setTimeout(() => {
        setStatusMessage(
          t('GroupImage.imageStatus', 'Image {current} of {total}', {
            current: sharedBrowserState.focusedIndex + 1,
            total: thumbnails.length,
          })
        )
      }, 300)
    }
    return () => clearTimeout(statusTimerRef.current)
  }, [sharedBrowserState.focusedIndex, thumbnails, t])

  // Fix 7/8: Detect out-of-range navigation attempts and announce the valid range
  const wrappedSetFocusedIndex = useCallback(
    (index) => {
      if (!thumbnails?.length) return
      if (index < 0 || index >= thumbnails.length) {
        setStatusMessage(
          t('GroupImage.imageRangeError', 'Enter a number between 1 and {total}', {
            total: thumbnails.length,
          })
        )
        return
      }
      setFocusedIndex(index)
    },
    [setFocusedIndex, thumbnails, t]
  )

  // Fix 2: Patch invalid ARIA roles produced by the library in divide mode (hoverable grid items)
  useEffect(() => {
    if (!updateDividersHoverButtonProps || !sectionRef.current) {
      return undefined
    }
    const section = sectionRef.current
    let rafId
    let observer

    const patchAriaRoles = () => {
      rafId = requestAnimationFrame(() => {
        observer.disconnect()
        patchGridAriaRoles(section)
        observer.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] })
      })
    }

    observer = new MutationObserver(patchAriaRoles)
    observer.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] })
    patchAriaRoles()

    return () => {
      observer.disconnect()
      cancelAnimationFrame(rafId)
    }
  }, [updateDividersHoverButtonProps])

  // Fix 12: Dismiss tooltips on Escape key (scoped to this section only)
  // TODO: open ticket in zion-ui to handle Escape inside tooltip components natively so this
  // workaround can be removed. Note: blurring the active element drops focus to body — a
  // tooltip fix in zion-ui should restore focus to the trigger instead.
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return undefined
    const handleEscapeTooltip = (e) => {
      if (e.key === 'Escape' && section.querySelector('[role="tooltip"]') && section.contains(document.activeElement)) {
        document.activeElement?.blur()
      }
    }
    section.addEventListener('keydown', handleEscapeTooltip)
    return () => section.removeEventListener('keydown', handleEscapeTooltip)
  }, [])

  // Fix 1: Keyboard navigation for Home/End/Space in grid view
  // TODO: open ticket in zion-image-viewer to add Home/End/Space handling to ImageGridBase's
  // onKeyDown so every ComboViewer consumer gets these keys without needing their own handler.
  const handleGridKeyDown = useCallback(
    (e) => {
      if (query?.grid !== 'on' || !thumbnails?.length) return
      if (e.key === ' ') {
        if (e.target.closest('button, a, [role="button"]')) return
        e.preventDefault()
        setGridParamInURL(false)
      } else if (e.key === 'Home') {
        e.preventDefault()
        setFocusedIndex(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        setFocusedIndex(thumbnails.length - 1)
      }
    },
    [query?.grid, thumbnails, setFocusedIndex, setGridParamInURL]
  )

  debug({
    currentImageArk,
    focusedIndex: sharedBrowserState.focusedIndex,
    ribbonImages,
    thumbnails,
    imageApids,
    loading,
    showImageGrid: query?.grid === 'on',
    sharedBrowserState,
  })

  // Commented out for now because of jira LCOM-1258
  // useKeyboardShortcuts({
  //   handlers: {
  //     KeyI: {
  //       action: () => infoSheetOverlay?.handleClick(),
  //       title: t('toggle-infosheet.button', 'Toggle Panel'),
  //     },
  //   },
  //   enabled: true,
  // })

  return !errorStatus ? (
    <>
      {loading ? (
        <Row alignX="center" alignY="middle">
          <NoticeLoading size="md" />
        </Row>
      ) : (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- section is a keyboard-interaction container for the ARIA listbox pattern
        <section
          ref={sectionRef}
          css={[viewerWrapperCss, gridA11yCss]}
          data-theme={theme}
          aria-label={t('film-viewer.accessibility-heading', 'Image Viewer')}
          style={viewerContainerStyle}
          onKeyDown={handleGridKeyDown}
        >
          <div css={visuallyHiddenCss} aria-live="polite" role="status">
            {statusMessage}
          </div>
          <ComboViewer
            {...imageViewer}
            setFocusedIndex={wrappedSetFocusedIndex}
            focusedIndex={modifiedThumbnails ? sharedBrowserState.focusedIndex : 0}
            showImageGrid={query?.grid === 'on'}
            setShowImageGrid={setGridParamInURL}
            setColumnCount={setColumnCount}
            columnCount={atWidth({ default: 3, md: columnCount })}
            images={modifiedThumbnails}
            ImageComponent={StaticImageViewer}
            adjustmentControls={<DarkModeControl />}
            onAdjustmentControlReset={handleAdjustmentControlReset}
            downloadControl
            onDownload={handleDownloadEvent}
            sections={sections}
            hoverButtonProps={updateDividersHoverButtonProps}
            controls={
              <Controls
                {...{
                  viewMode,
                  setViewMode,
                }}
              />
            }
            gridControls={
              <Controls
                {...{
                  viewMode,
                  setViewMode,
                }}
              />
            }
            numbered
            {...initialMaintainZoomPanProps}
            minZoom={0.8}
          />
          {query?.grid !== 'on' && ribbonImages.length && (
            <RibbonViewer
              focusedIndex={sharedBrowserState.focusedIndex > -1 ? sharedBrowserState.focusedIndex : 0}
              setFocusedIndex={setFocusedIndex}
              images={ribbonImages}
            />
          )}
        </section>
      )}
    </>
  ) : (
    <>
      <ImageGroupError errorState={errorStatus} view={view} />
      <SharedUsabilla />
    </>
  )
}
